import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Globe, LayoutDashboard, LoaderCircle, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Badge } from '@/_shared/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { formatDate } from '@/_shared/lib/format';

export function DashboardList(): React.ReactElement {
  usePageTitle('Dashboards');
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(orpc.dashboard.list.queryOptions({ input: {} }));

  const deleteMutation = useMutation(
    orpc.dashboard.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
        });
      },
    }),
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      deleteMutation.mutate({ id });
    },
    [deleteMutation],
  );

  const allDashboards = useMemo(() => data?.dashboards ?? [], [data?.dashboards]);

  const dashboards = useMemo(() => {
    if (!search.trim()) return allDashboards;
    const q = search.toLowerCase();
    return allDashboards.filter(
      (d) => d.title.toLowerCase().includes(q) || (d.description?.toLowerCase().includes(q) ?? false),
    );
  }, [allDashboards, search]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="mb-2 flex items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-foreground">Dashboards</h1>
          {allDashboards.length > 0 && (
            <SearchInput value={search} onChange={setSearch} placeholder="Search dashboards..." className="max-w-xs" />
          )}
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">Analytics dashboards generated from your conversations.</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <CircleAlert size={32} className="mb-4 text-red-500" />
            <h2 className="text-base font-medium text-foreground">Failed to load dashboards</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {error.message || 'An unexpected error occurred. Please try again.'}
            </p>
          </div>
        )}

        {!isLoading && !error && dashboards.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <LayoutDashboard size={48} className="mb-4 text-muted-foreground/30" />
            <h2 className="text-base font-medium text-foreground">No dashboards yet</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Create one by starting a chat conversation and asking the assistant to build a dashboard for you.
            </p>
          </div>
        )}

        {!isLoading && !error && dashboards.length > 0 && (
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 px-6 py-6 sm:grid-cols-2">
            {dashboards.map((dashboard) => (
              <Link
                key={dashboard.id}
                to={wp(`/d/${dashboard.slug}`)}
                className="group block transition-opacity hover:opacity-80"
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm">{dashboard.title}</CardTitle>
                      <div className="flex shrink-0 items-center gap-1">
                        {dashboard.isPublic && (
                          <Badge variant="outline" className="shrink-0 gap-1 text-[0.625rem]">
                            <Globe size={10} />
                            Public
                          </Badge>
                        )}
                        <button
                          onClick={(e) => handleDelete(e, dashboard.id)}
                          className="rounded p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                          aria-label={`Delete ${dashboard.title}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {dashboard.description && (
                      <CardDescription className="line-clamp-2 text-xs">{dashboard.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {dashboard.spec.panels.length} {dashboard.spec.panels.length === 1 ? 'panel' : 'panels'}
                      </span>
                      <span>{formatDate(dashboard.updatedAt)}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
